import React, { useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration: number = 3000) => {
    const id = Math.random().toString(36).substring(7);
    const newToast: Toast = { id, message, type };
    
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, duration);
  }, []);

  const getToastStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-neon-green/20 border-neon-green text-neon-green';
      case 'error':
        return 'bg-neon-pink/20 border-neon-pink text-neon-pink';
      case 'warning':
        return 'bg-neon-yellow/10 border-neon-yellow text-neon-yellow';
      case 'info':
      default:
        return 'bg-neon-purple/10 border-neon-purple text-neon-purple';
    }
  };

  const getToastIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return '🕹️';
      case 'error':
        return '💀';
      case 'warning':
        return '🪙';
      case 'info':
      default:
        return '👾';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-2 left-1/2 -translate-x-1/2 sm:top-4 sm:left-auto sm:-translate-x-0 sm:right-4 z-[9999] flex flex-col gap-2 max-w-[90vw] sm:max-w-sm pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              font-retro text-xs sm:text-sm p-3 sm:p-4 rounded-lg border-2 
              ${getToastStyles(toast.type)}
              animate-[slideIn_0.3s_ease-out]
              shadow-lg backdrop-blur-sm
              pointer-events-auto
            `}
            style={{
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{getToastIcon(toast.type)}</span>
              <p className="flex-1">{toast.message}</p>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
